import { useCallback, useMemo } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { SageAuth, SageAuthLoginOptions } from './SageAuth';
import webAuth from 'src/components/auth/AuthConfigManager';
import { authorizationParams } from 'src/constants/Auth0';

/** Today's behaviour, unchanged: the SPA SDK owns the session and the legacy library sends the OTP. */
export function useWebAuth(): SageAuth {
    const {
        isLoading,
        isAuthenticated,
        user,
        error,
        getAccessTokenSilently,
        getIdTokenClaims,
        loginWithRedirect,
        logout,
    } = useAuth0();

    const getAccessToken = useCallback(
        () => getAccessTokenSilently({ authorizationParams }),
        [getAccessTokenSilently]
    );

    const getIdTokenExpiry = useCallback(
        async () => (await getIdTokenClaims())?.exp,
        [getIdTokenClaims]
    );

    const login = useCallback(
        async (options?: SageAuthLoginOptions) => {
            await loginWithRedirect(
                options?.connection
                    ? {
                          authorizationParams: {
                              connection: options.connection,
                              login_hint: options.loginHint,
                              prompt: options.forceLogin ? 'login' : undefined,
                          },
                      }
                    : undefined
            );
        },
        [loginWithRedirect]
    );

    const sendOtp = useCallback(
        (phoneNumber: string) =>
            new Promise<void>((resolve, reject) => {
                webAuth.passwordlessStart(
                    { connection: 'sms', send: 'code', phoneNumber },
                    (sendError) => (sendError ? reject(sendError) : resolve())
                );
            }),
        []
    );

    const verifyOtp = useCallback(
        (phoneNumber: string, code: string) =>
            new Promise<void>((resolve, reject) => {
                webAuth.passwordlessLogin(
                    { connection: 'sms', phoneNumber, verificationCode: code },
                    (loginError) => (loginError ? reject(loginError) : resolve())
                );
            }),
        []
    );

    const doLogout = useCallback(
        async ({ returnTo }: { returnTo: string }) => {
            await logout({
                clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
                logoutParams: { returnTo },
            });
        },
        [logout]
    );

    return useMemo(
        () => ({
            owner: 'web' as const,
            tenant: undefined,
            isLoading,
            isAuthenticated,
            user,
            error,
            getAccessToken,
            getIdTokenExpiry,
            login,
            sendOtp,
            verifyOtp,
            logout: doLogout,
        }),
        [
            isLoading,
            isAuthenticated,
            user,
            error,
            getAccessToken,
            getIdTokenExpiry,
            login,
            sendOtp,
            verifyOtp,
            doLogout,
        ]
    );
}
